import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MdCheckCircle } from "react-icons/md";
import { FiArrowLeft } from "react-icons/fi";
import AuthCard from "../../components/auth/AuthCard";
import AuthHeaderLink from "../../components/auth/AuthHeaderLink";
import InputField from "../../components/auth/InputField";
import PasswordField from "../../components/auth/PasswordField";
import OtpInput from "../../components/auth/OtpInput";
import { forgotPasswordRequestApi, forgotPasswordVerifyApi, forgotPasswordResetApi } from "./api";

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

const maskEmail = (email = "") => {
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const visible = name.slice(0, name.length > 2 ? 2 : 1);
  return `${visible}${"•".repeat(Math.max(name.length - visible.length, 2))}@${domain}`;
};

export default function ForgotPassword() {
  // steps: "email" → "reset" → "success"
  const [step, setStep] = useState("email");

  // step: email
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");

  // step: reset
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  // resend timer
  const [resendIn, setResendIn] = useState(0);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);

  const startResendTimer = useCallback(() => {
    setResendIn(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── Step 1: request OTP ──────────────────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) { setEmailError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError("Enter a valid work email address."); return; }
    setEmailError("");
    setServerError("");
    setSending(true);
    try {
      const res = await forgotPasswordRequestApi({ email: email.trim().toLowerCase() });
      if (res.status) {
        setOtp("");
        setOtpError("");
        setNewPassword("");
        setConfirmPassword("");
        setFieldErrors({});
        setResetError("");
        setStep("reset");
        startResendTimer();
      } else {
        setServerError(res.message || "Could not send reset code. Please try again.");
      }
    } catch (err) {
      setServerError(err?.response?.data?.message || "Could not send reset code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setOtpError("");
    try {
      const res = await forgotPasswordRequestApi({ email: email.trim().toLowerCase() });
      if (res.status) {
        setOtp("");
        startResendTimer();
      } else {
        setOtpError(res.message || "Couldn't resend the code. Please try again.");
      }
    } catch (err) {
      setOtpError(err?.response?.data?.message || "Couldn't resend the code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // ── Step 2: verify OTP + reset password ─────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    const errs = {};
    if (otp.length !== OTP_LENGTH) { setOtpError(`Enter the ${OTP_LENGTH}-digit code.`); return; }
    setOtpError("");
    if (!newPassword) errs.newPassword = "New password is required.";
    else if (newPassword.length < 8) errs.newPassword = "Password must be at least 8 characters.";
    if (!confirmPassword) errs.confirmPassword = "Please confirm your password.";
    else if (newPassword && confirmPassword !== newPassword) errs.confirmPassword = "Passwords do not match.";
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setResetError("");
    setResetting(true);
    try {
      // Step 1: get token by verifying OTP
      const verifyRes = await forgotPasswordVerifyApi({
        email: email.trim().toLowerCase(),
        otp: Number(otp),
      });
      if (!verifyRes.status) {
        setOtpError(verifyRes.message || "Invalid or expired code. Please try again.");
        setResetting(false);
        return;
      }
      // Step 2: reset password using token
      const resetRes = await forgotPasswordResetApi({
        token: verifyRes.otpToken,
        newPassword,
      });
      if (resetRes.status) {
        clearInterval(timerRef.current);
        setStep("success");
      } else {
        setResetError(resetRes.message || "Could not reset password. Please try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not reset password. Please try again.";
      if (err?.response?.status === 400) setOtpError(msg);
      else setResetError(msg);
    } finally {
      setResetting(false);
    }
  };

  // ── Success screen ───────────────────────────────────────────────────────
  if (step === "success") {
    return (
      <AuthCard>
        <div className="flex flex-col items-center text-center py-4 gap-5">
          <div className="w-14 h-14 rounded-2xl bg-success_bg border border-success_border flex items-center justify-center">
            <MdCheckCircle size={28} className="text-success" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
              Password reset!
            </h1>
            <p className="text-sm text-text_muted mt-2 leading-relaxed max-w-[300px]">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
          </div>
          <Link
            to="/login"
            className="w-full flex items-center justify-center gap-2 bg-primary text-white text-[13.5px] font-semibold py-2.5 rounded"
          >
            Back to Sign In
          </Link>
        </div>
      </AuthCard>
    );
  }

  // ── Reset step: OTP + new password ──────────────────────────────────────
  if (step === "reset") {
    return (
      <AuthCard
        headerRight={<AuthHeaderLink prompt="Remember your password?" to="/login" label="Sign in" />}
      >
        <button
          onClick={() => { clearInterval(timerRef.current); setStep("email"); setOtp(""); setOtpError(""); }}
          className="flex items-center gap-1.5 text-[13px] text-text_muted mb-6"
        >
          <FiArrowLeft size={15} /> Back
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
            Check your email
          </h1>
          <p className="text-[13.5px] text-text_muted mt-1.5 leading-relaxed">
            Enter the {OTP_LENGTH}-digit code sent to{" "}
            <span className="font-medium text-text_secondary">{maskEmail(email)}</span>
            , then set your new password.
          </p>
        </div>

        <form onSubmit={handleReset} className="flex flex-col gap-4">
          <OtpInput
            length={OTP_LENGTH}
            value={otp}
            onChange={(v) => { setOtp(v); if (otpError) setOtpError(""); }}
            error={otpError}
            disabled={resetting}
            autoFocus
          />

          <PasswordField
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); if (fieldErrors.newPassword) setFieldErrors((p) => ({ ...p, newPassword: "" })); }}
            placeholder="Min. 8 characters"
            error={fieldErrors.newPassword}
            autoComplete="new-password"
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); if (fieldErrors.confirmPassword) setFieldErrors((p) => ({ ...p, confirmPassword: "" })); }}
            placeholder="Repeat your password"
            error={fieldErrors.confirmPassword}
            autoComplete="new-password"
          />

          <AnimatePresence>
            {resetError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <div className="rounded bg-danger_bg border border-danger_border px-3.5 py-2.5 text-[13px] text-danger">
                  {resetError}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={resetting}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white text-[13.5px] font-semibold py-2.5 rounded disabled:opacity-55 disabled:cursor-not-allowed"
          >
            {resetting ? (
              <div className="w-[18px] h-[18px] border-2 border-white/25 border-t-white rounded-full animate-spin" />
            ) : (
              "Reset Password"
            )}
          </button>

          <div className="text-center text-[13px] text-text_muted">
            Didn't receive the code?{" "}
            {resendIn > 0 ? (
              <span className="text-text_secondary">
                Resend in <span className="font-semibold text-text_primary">{resendIn}s</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-primary font-semibold hover:underline underline-offset-2 disabled:opacity-55"
              >
                {resending ? "Sending…" : "Resend code"}
              </button>
            )}
          </div>
        </form>
      </AuthCard>
    );
  }

  // ── Email step ───────────────────────────────────────────────────────────
  return (
    <AuthCard
      headerRight={<AuthHeaderLink prompt="Remember your password?" to="/login" label="Sign in" />}
    >
      <Link
        to="/login"
        className="flex items-center gap-1.5 text-[13px] text-text_muted mb-6 w-fit"
      >
        <FiArrowLeft size={15} /> Back to login
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text_primary tracking-tight leading-tight">
          Forgot password?
        </h1>
        <p className="text-[13.5px] text-text_muted mt-1.5 leading-relaxed">
          Enter your registered work email address and we'll send you a verification code to reset your password.
        </p>
      </div>

      <form onSubmit={handleRequestOtp} noValidate className="flex flex-col gap-4">
        <InputField
          id="email"
          label="Work Email"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); if (serverError) setServerError(""); }}
          placeholder="Enter your work email address"
          error={emailError}
          autoComplete="email"
        />

        <AnimatePresence>
          {serverError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="rounded bg-danger_bg border border-danger_border px-3.5 py-2.5 text-[13px] text-danger">
                {serverError}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={sending}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white text-[13.5px] font-semibold py-2.5 rounded disabled:opacity-55 disabled:cursor-not-allowed mt-1"
        >
          {sending ? (
            <div className="w-[18px] h-[18px] border-2 border-white/25 border-t-white rounded-full animate-spin" />
          ) : (
            "Send Reset Code"
          )}
        </button>
      </form>

    </AuthCard>
  );
}
